import { getSubmStatusLabel } from '@/api/projects/submission'
import { getAttachments } from '@/api/docs/request'
import { getLink } from '@/api/postman/util'
import { getPurchase, getPurchaseNumber } from '@/api/projects/util'
import { FOLDER_STATUS } from '@/utils/consts'
import { completionType, submissionStatus } from '@/utils/locale'
import dateUtil from '@/utils/dateUtil'
import userHelper from '@/utils/userHelper'
import i18n from '@/utils/i18n'
import currencyFormatter from 'currency-formatter'
import lodash from 'lodash'
import store from 'store'
import { calculatePercentage, formatAmount } from '@/utils/amountUtil'
import { isFinalSubmission } from '@/modules/projects/submission-request/utils'

export async function createSubmissionRequest (originalData = {}, lastOrder = 0) {
  const currentRegion = store.getters['regions/currentRegion'] || {}
  const project = lodash.cloneDeep(originalData)
  const purchase = getPurchase(project)
  const currency = purchase.currencyCode || currentRegion.currency
  const retention = lodash.get(project, 'retention.percentValue')
  let request = {
    currency,
    vendorName: deep(purchase, 'company.name'),
    poNumber: getPurchaseNumber(project.numberType, purchase),
    poDescription: deep(purchase, 'description'),
    amount: `${formatAmount(Number(deep(purchase, 'amount')))}`,
    site: deep(purchase, 'siteId'),
    handledBy: userHelper.getUserName(deep(project, 'manager.username'), deep(project, 'manager.displayName')),
    retention: retention ? `${retention}%` : '',
    link: getLink('projectDetail', project.id),
    numberType: deep(project, 'numberType'),
    typeLabel: project.numberType === 'CONTRACT' ? i18n.t('project.type.contract') : i18n.t('project.type.po'),
  }
  const haveDeduction = project.deduction && project.deduction.amount > 0
  const totalAmountNumber = Number(lodash.get(purchase, 'amount', 0))
  const deductionAmount = lodash.get(project, 'deduction.amount', 0)
  const currentRevisedAmount = haveDeduction ? totalAmountNumber - Number(deductionAmount) : totalAmountNumber

  if (haveDeduction) {
    request.deduction = formatAmount(deductionAmount)
    request.revisedAmount = formatAmount(currentRevisedAmount)
  }
  const retentionAmount = (retention || 0) * currentRevisedAmount / 100
  request.retentionAmount = retentionAmount ? formatAmount(retentionAmount) : currencyFormatter.format(0, 'USD')
  request.prNumber = deep(purchase, ['tenders', 0, 'prNumber'])

  let lastApprTime
  let previousAmount = 0

  if (project.submissions) {
    const submissions = lodash.sortBy(project.submissions, 'order')
    const filteredSubmissions = submissions.filter(submission => +submission.order <= +lastOrder)
    await Promise.all(filteredSubmissions.map(async submission => {
      if (submission.order === lastOrder) {
        submission.needHighlight = true
        lastApprTime = deep(submission, ['actionHistory', 0, 'updatedAt'])
      }
      let revisedAmount = Number(deep(purchase, 'amount'))
      let remainingBalance = revisedAmount - previousAmount
      if (submission.deductionAmount) {
        revisedAmount -= Number(submission.deductionAmount)
        remainingBalance -= Number(submission.deductionAmount)
      }

      submission.completedDate = dateUtil.formatDate(submission.completedDate)
      submission.submittedDate = dateUtil.formatDate(submission.submittedDate, true)
      submission.completionMsg = getCompletionMsg(submission)
      submission.isFinal = isFinalSubmission(submission, retention > 0)
      submission.deductionAmount = submission.deductionAmount && formatAmount(submission.deductionAmount)
      submission.poAmount = formatAmount(request.amount)
      submission.currentAmount = formatAmount(submission.completedAmount)
      submission.currentAmountPct = calculatePercentage(submission.completedAmount, revisedAmount)
      submission.previousAmount = previousAmount
        ? formatAmount(previousAmount)
        : currencyFormatter.format(0, 'USD')
      submission.previousAmountPct = calculatePercentage(previousAmount, revisedAmount)

      remainingBalance -= submission.completedAmount
      if (remainingBalance === 0) {
        submission.remainingBalance = currencyFormatter.format(0, 'USD')
      } else {
        submission.remainingBalance = formatAmount(remainingBalance)
      }
      submission.remainingBalancePct = calculatePercentage(remainingBalance, revisedAmount)
      const isAcceptedFinalPayment = remainingBalance > 0 &&
        submission.status === submissionStatus.APPR &&
        submission.completionType === completionType.FINAL_PAYMENT
      submission.remainingBalanceLabel = isAcceptedFinalPayment
        ? i18n.t('project.submission.remainingBalanceDeducted')
        : i18n.t('project.submission.remainingBalance')
      submission.status = getSubmStatusLabel(submission)[0]

      previousAmount += submission.completedAmount

      submission.files = await getAttachments(submission.id) || []
      submission.files.forEach(file => {
        file.createdAt = dateUtil.formatDate(file.createdAt, true, true)
      })

      return submission
    }))
    request.submissions = filteredSubmissions.reverse()
  }

  request.guidelineDesc = project.guidelineDesc
  request.poAttachments = project.poAttachmentId && await getAttachments(project.poAttachmentId)
  const awardedDate = deep(purchase, ['tenders', 0, 'awardedDate'])
  request.awardedDate = awardedDate ? dateUtil.formatDate(awardedDate, true) : null

  request.projectFiles = await getAttachments(project.id) || []
  request.projectFiles.forEach(file => {
    file.createdAt = dateUtil.formatDate(file.createdAt, true, true)
  })

  if (lastApprTime) {
    request.folders = await getApprovedDocTypes(project.folders, dateUtil.toMoment(lastApprTime).valueOf())
  }

  return request
}

async function getApprovedDocTypes (folders = [], lastApprTime) {
  const filteredFolders = folders.filter(folder => {
    if (folder.status !== FOLDER_STATUS.APPR) return false
    if (folder.actionHistory) {
      folder.status = i18n.t('accepted')
      const folderAcceptedTime = dateUtil.toMoment(deep(folder, ['actionHistory', 0, 'updatedAt']).valueOf())
      return lastApprTime >= folderAcceptedTime
    }

    return false
  })

  return Promise.all(filteredFolders.map(async folder => {
    const files = await getAttachments(folder.id) || []
    files.forEach(file => {
      file.createdAt = dateUtil.formatDate(file.createdAt, true, true)
    })
    folder.files = files
    return folder
  }))
}

export function getCompletionMsg (submission = {}) {
  let type = (submission || {}).completionType
  const retentionOrder = submission.retentionOrder
  let prefix = `${type} Completion`
  if (type === completionType.FINAL_PARTIAL) {
    prefix = submission.order > 1 ? i18n.t('project.submission.allWorksComp', [completionType.PARTIAL]) : i18n.t('project.submission.fullCompletion')
  } else if (type === completionType.FINAL_PAYMENT && !retentionOrder) {
    prefix = i18n.t('project.submission.finalPaymentCompletion')
  } else if (type === completionType.RETENTION_PAYMENT || type === completionType.FINAL_PAYMENT) {
    prefix = `${i18n.t('project.submission.retentionPayment', [retentionOrder])} Completed`
  }
  return `${prefix} as of ${dateUtil.formatDate(submission.completedDate) || ''}`
}
